import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { UseFieldArrayRemove, UseFieldArrayUpdate, useFormContext } from 'react-hook-form';
import { Dropdown } from '@app/components/form/Dropdown';
import { CustomerSelect } from '@app/components/form/selects/features/CustomerSelect';
import { PromotionConditionSelect } from '@app/components/form/selects/features/PromotionConditionSelect';
import { PromotionConditionOperator, PromotionConditionType } from '@app/common';
import { PackSelect } from '@app/components/form/selects/features/PackSelect';
import { NumberInput } from '@app/components/form/NumberInput';
import { ProductVariationSelect } from '@app/components/form/selects/features/ProductVariationSelect';
import { TagSelect } from '@app/components/form/selects/features/TagSelect';
import { OperatorSelect } from '@app/components/form/selects/features/OperatorSelect';

const PromotionConditionPacksFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <OperatorSelect
        name={`conditions.${index}.details.operator`}
        data={[PromotionConditionOperator.IN]} />

      <PackSelect
        name={`conditions.${index}.details.value`}
        isMultiple />

      <NumberInput
        name={`conditions.${index}.details.quantity`}
        min={1}
        label="Quantité"
      />
    </Fragment>
  )
}

const PromotionConditionProductVariationsFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <OperatorSelect
        name={`conditions.${index}.details.operator`}
        data={[PromotionConditionOperator.IN]} />

      <ProductVariationSelect
        name={`conditions.${index}.details.value`}
        isMultiple />

      <NumberInput
        name={`conditions.${index}.details.quantity`}
        min={1}
        label="Quantité"
      />
    </Fragment>
  )
}

const PromotionConditionTagsFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <OperatorSelect
        name={`conditions.${index}.details.operator`}
        data={[PromotionConditionOperator.IN, PromotionConditionOperator.NOT_IN]} />

      <TagSelect
        name={`conditions.${index}.details.value`}
        isMultiple />

      <NumberInput
        name={`conditions.${index}.details.quantity`}
        min={1}
        label="Quantité"
      />
    </Fragment>
  )
}

const PromotionConditionCustomersFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <OperatorSelect
        name={`conditions.${index}.details.operator`}
        data={[PromotionConditionOperator.IN]} />

      <CustomerSelect
        name={`conditions.${index}.details.value`}
        transform={{
          display: (value) => {
            if (value.phone) {
              return value.lastname + ' ' + value.firstname + ' - ' + value.phone
            }

            return value.lastname + ' ' + value.firstname
          },
        }}
        isMultiple />
    </Fragment>
  )
}

const PromotionConditionBirthdayWindowFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <NumberInput
        min={0}
        name={`conditions.${index}.details.daysBeforeBirthday`}
        label="Jours avant l'anniversaire"
      />

      <NumberInput
        min={0}
        name={`conditions.${index}.details.daysAfterBirthday`}
        label="Jours après l'anniversaire"
      />
    </Fragment>
  )
}

const PromotionConditionOrderTotalFields = ({ index }: { index: number }) => {
  return (
    <Fragment>
      <OperatorSelect
        name={`conditions.${index}.details.operator`}
        data={[PromotionConditionOperator.GREATER_THAN_OR_EQUAL, PromotionConditionOperator.LESS_THAN_OR_EQUAL]} />

      <NumberInput
        min={0}
        name={`conditions.${index}.details.value`}
        label="Montant (€)" />
    </Fragment>
  )
}

const getDefaultDetailsValue = (type: PromotionConditionType) => {
  switch (type) {
    case PromotionConditionType.PACKS:
      return {
        operator: PromotionConditionOperator.IN,
        value: [],
        quantity: 1,
      }

    case PromotionConditionType.PRODUCT_VARIATIONS:
      return {
        operator: PromotionConditionOperator.IN,
        value: [],
        quantity: 1,
      }

    // case PromotionConditionType.TAGS:
    //   return {
    //     operator: PromotionConditionOperator.IN,
    //     value: [],
    //     quantity: 1,
    //   }

    case PromotionConditionType.CUSTOMERS:
      return {
        operator: PromotionConditionOperator.IN,
        value: [],
        quantity: 1,
      }

    case PromotionConditionType.BIRTHDAY_WINDOW:
      return {
        daysBeforeBirthday: 0,
        daysAfterBirthday: 0,
      }

    case PromotionConditionType.ORDER_TOTAL:
      return {
        operator: PromotionConditionOperator.GREATER_THAN_OR_EQUAL,
        value: 0,
      }
  }
}


interface PromotionConditionFormProps {
  index: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<any>;
}

export const PromotionConditionForm = ({ index, isOpen, setIsOpen, remove, update }: PromotionConditionFormProps) => {
  const name = `Condition: ${index + 1}`;

  const { watch, formState: { errors } } = useFormContext();

  const [type, referenceId] = watch([`conditions.${index}.type`, `conditions.${index}.referenceId`]);
  const previousTypeRef = useRef(type);

  const error = Boolean(errors.conditions) ? "Veuillez vérifier les champs" : undefined;

  useEffect(() => {
    if (previousTypeRef.current !== type && previousTypeRef.current !== undefined) {
      update(index, {
        type: type,
        referenceId,
        details: getDefaultDetailsValue(type)
      });
    }
    previousTypeRef.current = type;
  }, [type, index, referenceId, update]);

  const Fields = useMemo(() => {
    switch (type) {
      case PromotionConditionType.PACKS:
        return <PromotionConditionPacksFields index={index} />

      case PromotionConditionType.PRODUCT_VARIATIONS:
        return <PromotionConditionProductVariationsFields index={index} />

      // case PromotionConditionType.TAGS:
      //   return <PromotionConditionTagsFields index={index} />

      case PromotionConditionType.CUSTOMERS:
        return <PromotionConditionCustomersFields index={index} />

      case PromotionConditionType.BIRTHDAY_WINDOW:
        return <PromotionConditionBirthdayWindowFields index={index} />

      case PromotionConditionType.ORDER_TOTAL:
        return <PromotionConditionOrderTotalFields index={index} />

      default:
        return null;
    }
  }, [type, index])

  return (
    <Dropdown
      label={name}
      error={error}
      isOpen={isOpen}
      handleOpen={setIsOpen}
      onDelete={() => remove(index)}>

      <PromotionConditionSelect name={`conditions.${index}.type`} />

      {Fields}

    </Dropdown>
  )
}